import { createRouter, createRoute, createRootRoute, Outlet, Navigate } from '@tanstack/react-router';
import Home from './pages/Home';
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import Docs from './pages/Docs';
import DocsIndex from './pages/DocsIndex';
import SupportDocs from './pages/SupportDocs';
import SupportArticle from './pages/SupportArticle';
import BlogPage from './pages/BlogPage';
import BlogLayout from './pages/blog/BlogLayout';
import BlogPostPage from './pages/blog/BlogPostPage';
import ApiKeysPage from './pages/ApiKeys';
import VerifyEmail from './pages/VerifyEmail';
import VerificationPending from './pages/VerificationPending';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import GltfCompressorPage from './pages/GltfCompressorPage';
import GltfGeneratorPage from './pages/GltfGeneratorPage';
import GltfToGlbPage from './pages/GltfToGlbPage';
import GltfValidatorPage from './pages/GltfValidatorPage';
import AiStudioPage from './pages/AiStudioPage';
import AiStudioCreatePage from './pages/AiStudioCreatePage';
import AiStudioModelsPage from './pages/AiStudioModelsPage';
import AiStudioModelDetailsPage from './pages/AiStudioModelDetailsPage';
import AiStudioLayout from './pages/AiStudioLayout';
import AiStudioBilling from './pages/AiStudioBilling';

// Import admin pages
import AdminIndexPage from './pages/Admin/AdminIndexPage';
import AdminUsersPage from './pages/Admin/AdminUsersPage';
import AdminJobsPage from './pages/Admin/AdminJobsPage';
import Admin3DJobsPage from './pages/Admin/Admin3DJobsPage';
import AdminJobDetailsPage from './pages/Admin/AdminJobDetailsPage';
import AdminAnalyticsPage from './pages/Admin/AdminAnalyticsPage';
import RevenueProjectionsPage from './pages/Admin/RevenueProjectionsPage';
import ConversionProjectionsPage from './pages/Admin/ConversionProjectionsPage';
import EmailMarketingPage from './pages/Admin/EmailMarketingPage';

import { Dashboard, Billing, Profile } from './app/pages';
import Container from './app/Container';
import { isSignedIn } from './auth';
import { getUserUsage } from './api'; // Need this for admin check
import { DEFAULT_LOCALE, locales } from './i18n';
import Presets from './app/pages/Presets';

const rootRoute = createRootRoute({
  component: () => <div><Outlet /></div>,
});

const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: Home,
});

// Translated homepages at /<code>, one per locale file in src/i18n/home
const localeHomeRoutes = locales
  .filter((locale) => locale.code !== DEFAULT_LOCALE)
  .map((locale) =>
    createRoute({
      getParentRoute: () => rootRoute,
      path: `/${locale.code}`,
      component: () => <Home locale={locale.code} />,
    })
  );

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app',
  component: () => {
    if (!isSignedIn()) {
      return <Navigate to="/sign-in" replace />;
    }
    return <Container>
      <Outlet />
    </Container>;
  },
})

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  component: SignIn,
});

const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-up',
  component: SignUp,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPassword,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPassword,
});

const verifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verify-email/$token',
  component: VerifyEmail,
});

const verificationPendingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verification-pending',
  component: VerificationPending,
});

const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/dashboard',
  component: Dashboard,
});

const presetsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/presets',
  component: Presets,
});

// TODO: add not found

const docsIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/docs',
  component: () => <DocsIndex />,
});

const docsApiRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/docs/api',
  component: () => <Docs />,
});

const docsSupportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/docs/support',
  component: () => <SupportDocs />,
});

const docsSupportArticleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/docs/support/$articleId',
  component: () => <SupportArticle />,
});

// Blog Parent/Layout Route
const blogRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/blog',
  component: BlogLayout,
});

// Blog Index Route (renders the list at /blog)
const blogIndexRoute = createRoute({
  getParentRoute: () => blogRoute,
  path: '/',
  component: BlogPage,
});

// Blog posts come from the registry in src/blog
const blogPostRoute = createRoute({
  getParentRoute: () => blogRoute,
  path: '/$slug',
  component: BlogPostPage,
});

const billingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/billing',
  component: Billing,
});

const profileRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/profile',
  component: Profile,
});

const apiKeysRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/api-keys',
  component: ApiKeysPage,
});

// --- Admin Routes ---
// This parent route handles the admin check before loading any admin child route
const adminRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin',
  beforeLoad: async ({ location }) => {
    // Ensure user is signed in (redundant check, appRoute does this)
    if (!isSignedIn()) {
      return {
        redirect: {
          to: '/sign-in',
          search: {
            redirect: location.href,
          },
        }
      }
    }
    // Fetch user data to check admin status
    // NOTE: This might double-fetch if AuthProvider already did.
    // A better integration would rely solely on AuthProvider's state,
    // but this requires passing the auth state to the route definition,
    // which can be complex depending on setup.
    try {
      const user = await getUserUsage();
      if (!user || !user.is_admin) {
        console.warn("Non-admin user attempted to access /app/admin. Redirecting.");
        return { redirect: { to: '/app/dashboard', replace: true } };
      }
      // User is admin, proceed to load the child route component
    } catch (error) {
      console.error("Failed to fetch user data for admin check:", error);
      // Redirect to dashboard on error as a safe default
      return { redirect: { to: '/app/dashboard', replace: true } };
    }
  },
  // Outlet component for nested admin routes
  component: () => <Outlet />,
});

const adminIndexRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/',
  component: AdminIndexPage,
});

const adminUsersRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/users',
  component: AdminUsersPage,
});

const adminJobsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/jobs',
  component: AdminJobsPage,
});

const admin3DJobsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/generation-jobs',
  component: Admin3DJobsPage,
});

const adminJobDetailsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/jobs/$jobId',
  component: AdminJobDetailsPage,
});

const adminAnalyticsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/analytics',
  component: AdminAnalyticsPage,
});

const adminRevenueProjectionsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/revenue-projections',
  component: RevenueProjectionsPage,
});

const adminConversionProjectionsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/conversion-projections',
  component: ConversionProjectionsPage,
});

const adminEmailMarketingRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/email-marketing',
  component: EmailMarketingPage,
});

// --- End Admin Routes ---

// Added aiStudioRoute
const aiStudioLayoutRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/ai-studio',
  component: AiStudioLayout,
});

const aiStudioIndexRoute = createRoute({
  getParentRoute: () => aiStudioLayoutRoute,
  path: '/',
  component: AiStudioPage,
});

const aiStudioCreateRoute = createRoute({
  getParentRoute: () => aiStudioLayoutRoute,
  path: '/create',
  component: AiStudioCreatePage,
});

const aiStudioModelsRoute = createRoute({
  getParentRoute: () => aiStudioLayoutRoute,
  path: '/models',
  component: AiStudioModelsPage,
});

const aiStudioModelDetailsRoute = createRoute({
  getParentRoute: () => aiStudioLayoutRoute,
  path: '/model/$jobId',
  component: AiStudioModelDetailsPage,
});

const aiStudioBillingRoute = createRoute({
  getParentRoute: () => aiStudioLayoutRoute,
  path: '/billing',
  component: AiStudioBilling,
});

// Added gltfGeneratorRoute
const gltfGeneratorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/gltf-generator',
  component: GltfGeneratorPage,
});

// Added gltfCompressorRoute
const gltfCompressorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/gltf-compressor',
  component: GltfCompressorPage,
});

// Added gltfToGlbRoute
const gltfToGlbRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/gltf-to-glb',
  component: GltfToGlbPage,
});

// Added gltfValidatorRoute
const gltfValidatorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/gltf-validator',
  component: GltfValidatorPage,
});

const router = createRouter({
  routeTree: rootRoute.addChildren([
    homeRoute,
    ...localeHomeRoutes,
    gltfGeneratorRoute,
    gltfCompressorRoute,
    gltfToGlbRoute,
    gltfValidatorRoute,
    docsIndexRoute,
    docsApiRoute,
    docsSupportRoute,
    docsSupportArticleRoute,
    // Update blog route nesting
    blogRoute.addChildren([
      blogIndexRoute,
      blogPostRoute,
    ]),
    signInRoute,
    signUpRoute,
    forgotPasswordRoute,
    resetPasswordRoute,
    verifyEmailRoute,
    verificationPendingRoute,
    appRoute.addChildren([
      dashboardRoute,
      aiStudioLayoutRoute.addChildren([
        aiStudioIndexRoute,
        aiStudioCreateRoute,
        aiStudioModelsRoute,
        aiStudioModelDetailsRoute,
        aiStudioBillingRoute,
      ]),
      presetsRoute,
      billingRoute,
      profileRoute,
      apiKeysRoute,
      adminRoute.addChildren([
        adminIndexRoute,
        adminUsersRoute,
        adminJobsRoute,
        admin3DJobsRoute,
        adminJobDetailsRoute,
        adminAnalyticsRoute,
        adminRevenueProjectionsRoute,
        adminConversionProjectionsRoute,
        adminEmailMarketingRoute
      ]),
    ]),
  ]),
});

export default router;
